import { useCallback, useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import Form from '@lib/components/ReactHookForm/FormContainer';
import { Steps } from '@lib/enums/form';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import { pageStepsId } from '@lib/enums/selectors';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useLibModalManagement, {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { useEdmDocumentationsLazyQuery } from '@lib/graphql-common';
import { GetLoadOptions } from '@lib/components/Select/types';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { getDocumentationsLoadOptions as getDocumentationsLoadOptionsFn } from 'utils/fetch/searchLists/documentationsSearchList';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import { UseSiteActionsResult } from '../utils/useSiteActions';
import getSiteFormFields from '../utils/getSiteFormFields';
import getSiteDefaultValuesFromQueryData from '../utils/getSiteDefaultValuesFromQueryData';
import SiteAddFormActions from './SiteAddFormActions';

const SELECT_DOCUMENTS_MODAL = 'select-documents';

interface Props {
  formRef: FormRefType;
  useSiteActionsResult: UseSiteActionsResult;
  useModalManagementResult: UseModalManagementResult;
}

function SiteAdd(props: Props) {
  const { formRef, useModalManagementResult, useSiteActionsResult } = props;
  const {
    siteQuery,
    countriesCodes,
    onCreate,
    onCreateUserCallback,
    usersLoadOptions,
    sectorsLoadOptions,
    responsiblePersonLoadOptions,
    createMutationResult,
    isSectorRequired,
  } = useSiteActionsResult;
  const { openModal, closeModal, isModalOpened } = useModalManagementResult;
  const openUserFormModal = () => openModal(Modals.UserFormModal);
  const closeUserFormModal = () => closeModal(Modals.UserFormModal);

  const { t } = useTranslation();

  const { getUserAccessByCompany }: ContextProps = useOutletContext();
  const isDocumentationPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const [fetchDocumentationsLazyQuery] = useEdmDocumentationsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getDocumentationsLoadOptions = useCallback<GetLoadOptions>(
    () => getDocumentationsLoadOptionsFn(fetchDocumentationsLazyQuery),
    [fetchDocumentationsLazyQuery],
  );

  // Documents modal: sub-area rows share the modal; we track which row's
  // `areas.{index}.documentationLinks` path is being edited via ref/state.
  const useDocumentsModalResult = useLibModalManagement([
    SELECT_DOCUMENTS_MODAL,
  ]);
  const activeDocsFieldNameRef = useRef<string>(
    SiteFieldNames.DocumentationLinks,
  );
  const [activeDocsFieldName, setActiveDocsFieldName] = useState<string>(
    SiteFieldNames.DocumentationLinks,
  );

  const openDocumentsModal = useCallback(
    (name?: string) => {
      const target = name || SiteFieldNames.DocumentationLinks;
      activeDocsFieldNameRef.current = target;
      setActiveDocsFieldName(target);
      useDocumentsModalResult.openModal(SELECT_DOCUMENTS_MODAL);
    },
    [useDocumentsModalResult],
  );

  const onCloseDocumentsModal = useCallback(() => {
    useDocumentsModalResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useDocumentsModalResult]);

  const onDocumentsSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          activeDocsFieldNameRef.current,
          values.length
            ? values.map((docId) => ({
                value: docId,
                visibleViaQr: visibilityMap?.[docId] ?? true,
              }))
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [formRef],
  );

  const isDocumentsModalOpen = useDocumentsModalResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  const documentationsInitialVisibilityMap = useMemo(() => {
    if (!isDocumentsModalOpen) return undefined;
    const list = _get(formRef.current?.values, activeDocsFieldName) as
      Array<{ value?: string; visibleViaQr?: boolean }> | undefined;
    if (!Array.isArray(list)) return {};
    return list.reduce<Record<string, boolean>>((acc, item) => {
      const id = String(item?.value ?? '');
      if (!id) return acc;
      acc[id] = item?.visibleViaQr ?? true;
      return acc;
    }, {});
  }, [isDocumentsModalOpen, activeDocsFieldName, formRef]);

  const fields = getSiteFormFields({
    countriesCodes,
    getDocumentationsLoadOptions,
    isSectorRequired,
    openDocumentsModal,
    usersLoadOptions,
    sectorsLoadOptions,
    openUserFormModal,
    responsiblePersonLoadOptions,
    t,
    assigneeIds: [],
  });

  const defaultValues = getSiteDefaultValuesFromQueryData({
    t,
  });

  if (siteQuery.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteQuery.error)}
        description={getDescriptionFromError(siteQuery.error)}
      />
    );
  }

  return (
    <>
      <div id={pageStepsId} />
      <DashboardContentMaxWidth>
        <PageBody withSteps>
          <WhiteBox verticalPaddings="large">
            <Form
              actionsComponent={SiteAddFormActions}
              defaultValues={defaultValues}
              fields={fields}
              formId="site"
              formRef={formRef}
              graphQLErrors={getGraphQLErrors(createMutationResult.error)}
              graphQLErrorsReplacePathMap={{
                'geojson.features': SiteFieldNames.LocationPoint,
                sectorId: SiteFieldNames.Sector,
              }}
              loading={createMutationResult.loading}
              onSubmit={onCreate}
              steps={[
                { title: t('site-step-1') },
                { title: t('site-step-2'), optional: true },
                { title: t('site-step-3'), optional: true },
              ]}
              stepsCount={Steps.step3}
              sourceAppendValues={{
                areas: {
                  name: '',
                  code: '',
                  address: '',
                  description: '',
                  [SiteFieldNames.DocumentationLinks]: [],
                },
                conductCodeSections: { title: '', body: '' },
              }}
            />
          </WhiteBox>
        </PageBody>
      </DashboardContentMaxWidth>
      <UserModalForm
        isOpen={isModalOpened(Modals.UserFormModal)}
        onClose={closeUserFormModal}
        onSubmitUserFormCallback={onCreateUserCallback}
        title={t('add-team-member')}
      />
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={formRef}
        fieldName={activeDocsFieldName}
        visibleViaQr={
          isDocumentationPlanLocked
            ? undefined
            : { initialVisibilityMap: documentationsInitialVisibilityMap }
        }
      />
    </>
  );
}

export default SiteAdd;
